import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'compact' | 'featured';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'featured') {
    return (
      <article className="group relative rounded-lg overflow-hidden bg-background-50 h-full">
        <Link to={`/article/${article.slug}`} className="block h-full cursor-pointer">
          <div className="relative h-full min-h-[320px]">
            <img
              src={article.imageUrl}
              alt={article.imageAlt}
              title={`${article.title} - 国产一区二区精选`}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/10"></div>
            <div className="absolute bottom-0 left-0 right-0 p-6">
              <span className="inline-block px-3 py-1 text-xs font-medium bg-primary-500 text-background-50 rounded-full mb-3">
                {article.category}
              </span>
              <h3 className="font-heading text-xl md:text-2xl font-bold text-white mb-2 line-clamp-2 group-hover:text-primary-200 transition-colors">
                {article.title}
              </h3>
              <p className="text-sm text-white/80 line-clamp-2 mb-3">
                {article.excerpt}
              </p>
              <div className="flex items-center gap-4 text-xs text-white/60">
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {article.publishDate}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  {article.readTime} 分钟阅读
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {article.views >= 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views}
                </span>
              </div>
            </div>
          </div>
        </Link>
      </article>
    );
  }

  if (variant === 'compact') {
    return (
      <article className="group flex gap-4 p-3 rounded-lg hover:bg-background-100 transition-colors">
        <Link to={`/article/${article.slug}`} className="shrink-0 cursor-pointer">
          <div className="w-24 h-16 rounded-md overflow-hidden">
            <img
              src={article.imageUrl}
              alt={article.imageAlt}
              title={article.title}
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            />
          </div>
        </Link>
        <div className="flex-1 min-w-0">
          <Link to={`/article/${article.slug}`} className="cursor-pointer">
            <h4 className="text-sm font-medium text-foreground-950 line-clamp-2 group-hover:text-primary-600 transition-colors">
              {article.title}
            </h4>
          </Link>
          <div className="flex items-center gap-3 mt-1.5 text-xs text-foreground-500">
            <span>{article.publishDate}</span>
            <span>{article.readTime} 分钟</span>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="group bg-background-50 rounded-lg overflow-hidden border border-background-200 hover:border-primary-200 transition-all duration-300">
      <Link to={`/article/${article.slug}`} className="cursor-pointer">
        <div className="relative h-48 overflow-hidden">
          <img
            src={article.imageUrl}
            alt={article.imageAlt}
            title={`${article.title} - 国产一区二区精选`}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute top-3 left-3">
            <span className="px-2.5 py-1 text-xs font-medium bg-primary-500 text-background-50 rounded-full">
              {article.category}
            </span>
          </div>
        </div>
      </Link>
      <div className="p-4">
        <Link to={`/article/${article.slug}`} className="cursor-pointer">
          <h3 className="font-heading text-base font-semibold text-foreground-950 line-clamp-2 mb-2 group-hover:text-primary-600 transition-colors">
            {article.title}
          </h3>
        </Link>
        <p className="text-sm text-foreground-600 line-clamp-2 mb-3">
          {article.excerpt}
        </p>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line"></i>
              {article.publishDate}
            </span>
            <span>{article.readTime} 分钟</span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {article.tags.slice(0, 2).map((tag) => (
              <span
                key={tag}
                className="px-2 py-0.5 text-xs rounded bg-secondary-100 text-secondary-700 whitespace-nowrap"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      </div>
    </article>
  );
}